import { createContext, useContext, useState, useCallback, useEffect } from "react";

import { setupWebPush, teardownWebPush } from "../utils/webPush";

const AuthContext = createContext(null);

// Only customer and dispatcher accounts have web-push backend routes —
// admin/support aren't wired up for it.
const PUSH_ROLES = ["user", "rider"];

export function AuthProvider({ children }) {
  const [account, setAccount] = useState(() => {
    const stored = localStorage.getItem("account");
    return stored ? JSON.parse(stored) : null; // { role, ...accountFields }
  });

  // Covers a page refresh/return visit with an already-stored session —
  // login() below only fires for a fresh login, not a restored one.
  useEffect(() => {
    if (account?.role && PUSH_ROLES.includes(account.role)) {
      setupWebPush(account.role);
    }
  }, []);

  const login = useCallback((token, role, accountData) => {
    const merged = { role, ...accountData };
    localStorage.setItem("token", token);
    localStorage.setItem("account", JSON.stringify(merged));
    setAccount(merged);
    if (PUSH_ROLES.includes(role)) setupWebPush(role);
  }, []);

  const updateAccount = useCallback((accountData) => {
    setAccount((prev) => {
      const merged = { ...prev, ...accountData };
      localStorage.setItem("account", JSON.stringify(merged));
      return merged;
    });
  }, []);

  const logout = useCallback(() => {
    if (account?.role && PUSH_ROLES.includes(account.role)) teardownWebPush(account.role);
    localStorage.removeItem("token");
    localStorage.removeItem("account");
    setAccount(null);
  }, [account]);

  return (
    <AuthContext.Provider value={{ account, login, logout, updateAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
